import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Trash2, Plus, GripVertical, ChevronUp, ChevronDown } from "lucide-react"

export function PodcastScriptEditor({
  initialSegments,
  onChange,
  disabled,
  speakers = [],
}) {
  // Own local state — not overwritten by polling
  const [segments, setSegments] = useState(() =>
    Array.isArray(initialSegments) ? initialSegments : []
  )
  const initialized = useRef(false)

  // Initialize once from prop, then never update from prop again
  useEffect(() => {
    if (!initialized.current && Array.isArray(initialSegments) && initialSegments.length > 0) {
      setSegments(initialSegments)
      initialized.current = true
    }
  }, [initialSegments])

  // Notify parent of changes
  useEffect(() => {
    onChange?.(segments)
  }, [segments]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateSegment = (index, field, value) => {
    setSegments((prev) =>
      prev.map((seg, i) => (i === index ? { ...seg, [field]: value } : seg))
    )
  }

  const removeSegment = (index) => {
    setSegments((prev) => prev.filter((_, i) => i !== index))
  }

  const addSegment = () => {
    const lastSpeaker = segments.length > 0
      ? segments[segments.length - 1].speaker
      : speakers[0] || "Speaker"
    // Alternate to the other speaker
    const nextSpeaker = speakers.find((s) => s !== lastSpeaker) || lastSpeaker
    setSegments((prev) => [...prev, { speaker: nextSpeaker, text: "" }])
  }

  const moveSegment = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= segments.length) return
    setSegments((prev) => {
      const next = [...prev]
      ;[next[index], next[newIndex]] = [next[newIndex], next[index]]
      return next
    })
  }

  if (segments.length === 0) {
    return (
      <p className="text-sm text-zinc-500 italic">No script segments available.</p>
    )
  }

  return (
    <div className="space-y-3">
      {segments.map((segment, index) => (
        <div
          key={index}
          className="group relative rounded-lg border border-zinc-200 bg-white p-3"
        >
          <div className="flex items-start gap-2">
            {/* Reorder controls */}
            {!disabled && (
              <div className="flex flex-col items-center gap-0.5 pt-1">
                <button
                  type="button"
                  onClick={() => moveSegment(index, -1)}
                  disabled={index === 0}
                  className="p-0.5 text-zinc-400 hover:text-zinc-600 disabled:opacity-30"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <GripVertical className="h-3.5 w-3.5 text-zinc-300" />
                <button
                  type="button"
                  onClick={() => moveSegment(index, 1)}
                  disabled={index === segments.length - 1}
                  className="p-0.5 text-zinc-400 hover:text-zinc-600 disabled:opacity-30"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="flex-1 space-y-2">
              {/* Speaker name */}
              <Select
                value={segment.speaker}
                onValueChange={(value) =>
                  updateSegment(index, "speaker", value)
                }
                disabled={disabled}
              >
                <SelectTrigger className="h-8 text-sm font-medium bg-zinc-50 border-zinc-200">
                  <SelectValue placeholder="Select speaker" />
                </SelectTrigger>
                <SelectContent>
                  {speakers.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Text content */}
              <Textarea
                value={segment.text}
                onChange={(e) =>
                  updateSegment(index, "text", e.target.value)
                }
                disabled={disabled}
                rows={3}
                className="text-sm resize-y"
                placeholder="What this speaker says..."
              />
            </div>

            {/* Remove button */}
            {!disabled && segments.length > 1 && (
              <button
                type="button"
                onClick={() => removeSegment(index)}
                className="p-1 text-zinc-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}

      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addSegment}
          className="w-full border-dashed"
        >
          <Plus className="h-4 w-4" />
          Add Segment
        </Button>
      )}
    </div>
  )
}
